/**
 * TypographyTab — font family, size, weight, alignment, and overflow controls.
 * Single responsibility: typography-related style properties.
 */

import React from 'react';
import type { StyleProperties } from '../types';
import { SliderInput } from '../shared/SliderInput';
import { SegmentedControl } from '../shared/SegmentedControl';
import { PresetChips } from '../shared/PresetChips';
import { SectionLabel } from '../shared/SectionLabel';

export interface TypographyTabProps {
  value: StyleProperties;
  onChange: (p: StyleProperties) => void;
  isDark: boolean;
}

const FONT_PRESETS = [
  { label: 'System', value: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif' },
  { label: 'Inter', value: 'Inter,sans-serif' },
  { label: 'Roboto', value: 'Roboto,sans-serif' },
  { label: 'Poppins', value: 'Poppins,sans-serif' },
  { label: 'Playfair', value: '"Playfair Display",serif' },
  { label: 'Courier', value: '"Courier New",monospace' },
];

const FONT_WEIGHT_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'medium', label: 'Medium' },
  { value: 'semibold', label: 'Semibold' },
  { value: 'bold', label: 'Bold' },
] as const;

const FONT_STYLE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'italic', label: 'Italic' },
] as const;

const TEXT_ALIGN_OPTIONS = [
  { value: 'left', label: 'L', title: 'Align Left' },
  { value: 'center', label: 'C', title: 'Align Center' },
  { value: 'right', label: 'R', title: 'Align Right' },
  { value: 'justify', label: 'J', title: 'Justify' },
] as const;

const TEXT_TRANSFORM_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'uppercase', label: 'UPPER' },
  { value: 'lowercase', label: 'lower' },
  { value: 'capitalize', label: 'Title' },
] as const;

const TEXT_DECORATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'underline', label: 'Underline' },
  { value: 'line-through', label: 'Strike' },
] as const;

const WHITESPACE_OPTIONS = [
  { value: 'normal', label: 'Wrap' },
  { value: 'nowrap', label: 'No-wrap' },
  { value: 'pre', label: 'Pre' },
] as const;

const TEXT_OVERFLOW_OPTIONS = [
  { value: 'clip', label: 'Clip' },
  { value: 'ellipsis', label: 'Ellipsis' },
] as const;

const WORD_BREAK_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'break-word', label: 'Break' },
] as const;

/**
 * TypographyTab — comprehensive font and text styling controls.
 */
export const TypographyTab: React.FC<TypographyTabProps> = ({ value, onChange, isDark }) => {
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const inputBg = isDark ? 'rgba(2,6,23,0.6)' : '#f8fafc';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';

  const stackStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  };

  const fontInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '5px 8px',
    borderRadius: 4,
    border: `1px solid ${borderColor}`,
    background: inputBg,
    color: textPrimary,
    fontSize: 12,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const fontLabelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
    marginBottom: 4,
    display: 'block',
  };

  return (
    <div style={stackStyle}>
      {/* Font Family */}
      <div>
        <span style={fontLabelStyle}>Font Family</span>
        <input
          type="text"
          value={value.fontFamily ?? ''}
          onChange={(e) => onChange({ ...value, fontFamily: e.target.value || undefined })}
          placeholder="e.g. Inter, sans-serif"
          style={fontInputStyle}
          aria-label="Font family"
        />
        <div style={{ marginTop: 4 }}>
          <PresetChips
            chips={FONT_PRESETS}
            value={value.fontFamily}
            onChange={(v) => onChange({ ...value, fontFamily: v })}
            isDark={isDark}
          />
        </div>
      </div>

      <SliderInput
        label="Font Size"
        value={value.fontSize}
        onChange={(v) => onChange({ ...value, fontSize: v })}
        min={8}
        max={72}
        step={1}
        unit="px"
        isDark={isDark}
      />

      <SegmentedControl
        label="Font Weight"
        options={FONT_WEIGHT_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={typeof value.fontWeight === 'string' ? value.fontWeight : undefined}
        onChange={(v) => onChange({ ...value, fontWeight: v as StyleProperties['fontWeight'] })}
        isDark={isDark}
      />

      <SegmentedControl
        label="Font Style"
        options={FONT_STYLE_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.fontStyle}
        onChange={(v) => onChange({ ...value, fontStyle: v as StyleProperties['fontStyle'] })}
        isDark={isDark}
      />

      <SectionLabel label="Alignment & Transform" isDark={isDark} />

      <SegmentedControl
        label="Text Align"
        options={TEXT_ALIGN_OPTIONS as unknown as Array<{ value: string; label: string; title?: string }>}
        value={value.textAlign}
        onChange={(v) => onChange({ ...value, textAlign: v as StyleProperties['textAlign'] })}
        isDark={isDark}
      />

      <SegmentedControl
        label="Text Transform"
        options={TEXT_TRANSFORM_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.textTransform}
        onChange={(v) => onChange({ ...value, textTransform: v as StyleProperties['textTransform'] })}
        isDark={isDark}
      />

      <SegmentedControl
        label="Text Decoration"
        options={TEXT_DECORATION_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.textDecoration}
        onChange={(v) => onChange({ ...value, textDecoration: v as StyleProperties['textDecoration'] })}
        isDark={isDark}
      />

      <SectionLabel label="Spacing" isDark={isDark} />

      <SliderInput
        label="Line Height"
        value={value.lineHeight}
        onChange={(v) => onChange({ ...value, lineHeight: v })}
        min={0.8}
        max={3.0}
        step={0.1}
        unit="x"
        isDark={isDark}
      />

      <SliderInput
        label="Letter Spacing"
        value={value.letterSpacing}
        onChange={(v) => onChange({ ...value, letterSpacing: v })}
        min={-2}
        max={10}
        step={0.5}
        unit="px"
        isDark={isDark}
      />

      <SectionLabel label="Overflow" isDark={isDark} />

      <SegmentedControl
        label="White Space"
        options={WHITESPACE_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.whiteSpace}
        onChange={(v) => onChange({ ...value, whiteSpace: v as StyleProperties['whiteSpace'] })}
        isDark={isDark}
      />

      <SegmentedControl
        label="Text Overflow"
        options={TEXT_OVERFLOW_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.textOverflow}
        onChange={(v) => onChange({ ...value, textOverflow: v as StyleProperties['textOverflow'] })}
        isDark={isDark}
      />

      <SegmentedControl
        label="Word Break"
        options={WORD_BREAK_OPTIONS as unknown as Array<{ value: string; label: string }>}
        value={value.wordBreak}
        onChange={(v) => onChange({ ...value, wordBreak: v as StyleProperties['wordBreak'] })}
        isDark={isDark}
      />
    </div>
  );
};

TypographyTab.displayName = 'TypographyTab';
